import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import API_BASE_URL from "../../config";
import './DeviceList.css';

const DeviceList = () => {
  const { t } = useTranslation();
  const [devices, setDevices] = useState([]);
  const [pingResults, setPingResults] = useState({});
  const [loading, setLoading] = useState("");
  const [removeLoading, setRemoveLoading] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  const fetchDevices = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/device-manage/list`);
      const json = await res.json();
      if (json.success) {
        setDevices(json.data);
        pingAllDevices(json.data);
      }
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchDevices(); }, [fetchDevices]);

  const pingAllDevices = (deviceList) => {
    deviceList.forEach(device => handlePing(device.ip_address));
  };

  const handlePing = async (ip) => {
    setLoading(ip);
    try {
      const res = await fetch(`${API_BASE_URL}/diagnostic-tools/ping-device`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ip })
      });
      const data = await res.json();
      const status = data.status === "success"
        ? `Online (${data.response_time_ms} ms)`
        : "Offline";
      setPingResults(prev => ({ ...prev, [ip]: status }));
    } catch {
      setPingResults(prev => ({ ...prev, [ip]: "Error" }));
    } finally {
      setLoading("");
    }
  };

  const handleRemove = async (id) => {
    if (!window.confirm(t("remove_confirm"))) return;
    setRemoveLoading(id);
    try {
      const res = await fetch(`${API_BASE_URL}/device-manage/delete/${id}`, { method: "DELETE" });
      const result = await res.json();
      if (result.success) setDevices(prev => prev.filter(d => d.id !== id));
      else alert(result.error || "Remove failed");
    } catch { console.error("Remove error"); }
    finally { setRemoveLoading(""); }
  };

  const getStatusClass = (ip) => {
    const status = pingResults[ip];
    if (!status) return "checking";
    if (status.includes("Online")) return "online";
    if (status.includes("Offline") || status === "Error") return "offline";
    return "checking";
  };

  const filteredDevices = devices.filter(d =>
    (d.hostname || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (d.ip_address || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (d.vendor_name || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="device-list-page-bg">
      <div className="dl-container">
        <div className="dl-header-card">
          <h2 className="dl-title">{t("device_details")}</h2>
          <div className="dl-header-actions">
            <input
              type="text"
              placeholder="Search hostname, IP or vendor..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="dl-search-input"
            />
            <button className="dl-btn login" onClick={() => navigate("/device-manage/add-device")}>
              + {t("add_device")}
            </button>
          </div>
        </div>

        <ul className="dl-list">
          {filteredDevices.map(device => {
            const ip = device.ip_address;
            const id = device.id;
            const statusText = loading === ip ? "Checking..." : (pingResults[ip] || "Checking...");
            const isExpanded = expandedId === id;

            return (
              <li key={id} className="dl-item">
                <div className="dl-item-main">
                  <div className="dl-info">
                    <div className={`dl-status-dot ${getStatusClass(ip)}`}></div>
                    <div className="dl-meta">
                      <strong className="dl-hostname">{device.hostname}</strong>
                      <div style={{ fontSize: "0.8rem", color: "#94a3b8" }}>
                        {device.vendor_name} {device.model_name} | {ip}
                      </div>
                    </div>
                    <span className={`dl-status-text ${getStatusClass(ip) === "online" ? "text-online" : "text-offline"}`}>
                      {statusText}
                    </span>
                  </div>

                  <div className="dl-actions">
                    <button className="dl-btn info" onClick={() => setExpandedId(isExpanded ? null : id)}>
                      {isExpanded ? t("hide") : t("details")}
                    </button>
                    {!isExpanded && (
                      <div className="dl-desktop-only-btns">
                        <button className="dl-btn refresh" onClick={() => handlePing(ip)} disabled={loading === ip}>
                          {loading === ip ? "..." : t("refresh")}
                        </button>
                        <button className="dl-btn edit" onClick={() => navigate(`/device-manage/edit-device/${id}`)}>
                          {t("edit")}
                        </button>
                        <button className="dl-btn delete" onClick={() => handleRemove(id)} disabled={removeLoading === id}>
                          {removeLoading === id ? "..." : t("remove")}
                        </button>
                        <button className="dl-btn login" onClick={() => handleLogin(ip)}>{t("login")}</button>
                      </div>
                    )}
                  </div>
                </div>

                {isExpanded && (
                  <div className="dl-details-box">
                    <div className="details-grid">
                      <p><strong>IP:</strong> {ip}</p>
                      <p><strong>Type:</strong> {device.device_type}</p>
                      <p><strong>Vendor:</strong> {device.vendor_name}</p>
                      <p><strong>Model:</strong> {device.model_name}</p>
                      <p><strong>Driver:</strong> {device.driver_file}</p>
                      <p><strong>Log Method:</strong> {device.log_method}</p>
                      <p><strong>POP:</strong> {device.pop_name || "-"}</p>
                      <p><strong>Credential:</strong> {device.credential_label || "-"}</p>
                      <p><strong>Serial:</strong> {device.serial_no || "-"}</p>
                      <p><strong>MAC:</strong> {device.mac_address || "-"}</p>
                    </div>
                    <div className="dl-mobile-actions">
                      <button className="dl-btn edit" onClick={() => navigate(`/device-manage/edit-device/${id}`)}>
                        {t("edit")}
                      </button>
                      <button className="dl-btn login" onClick={() => window.open(`http://${ip}`, "_blank")}>
                        {t("login")}
                      </button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default DeviceList;